import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pencil, Plus, Sparkles, Trash2 } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';
import { useToast } from '../../hooks/useToast';
import { useConfirmDialog } from '../../components/common/useConfirmDialog';
// 生成キューのみを購読する (Context 4分割後の個別フック)。
import { useGenerationJobs } from '../../components/generation/state/jobsContext';
import { useCreditRates } from '../../features/organizations/hooks';
import { motionJobProgressPatch } from '../../features/motion/jobs';
import {
  createBrandCutGenerationJob,
  createBrandCutReviseJob,
  waitForJobCompletion,
  type BrandCutGenerationParams,
  type BrandCutGenerationResult,
} from '../../services/asyncJobApi';
import {
  uploadTemporaryMaterial,
  fetchMaterialById,
  updateCloudMaterial,
  updateTemporaryMaterial,
} from '../../features/materials/api';
import type { TemporaryMaterial, CloudMaterial } from '../../features/materials/materialsSlice';
import BrandCutModal, { type BrandCutModalLogo } from '../../components/BrandCutModal';
import { HelpTooltip } from '../../components/common/HelpTooltip';
import { MaterialsPickerModal } from '../../components/materials/MaterialsPickerModal';
import { TemporaryPreviewModal } from '../../components/materials/TemporaryPreviewModal';
import { CloudMaterialsModal } from '../../components/materials/CloudMaterialsModal';

/** モーショングラフィックス生成の採点要約 (backend brand_cuts.motion_summary)。動画 AI 生成・アップロードは null。 */
interface BrandCutMotionSummary {
  scores: Partial<Record<'logo' | 'text' | 'colors' | 'completion' | 'direction', number>>;
  average: number;
  passed: boolean;
  /** 採用した版が何回目か (1 = 初稿) */
  attempt: number | null;
  critique_rounds: number;
  /** 「AI で修正」できるか (生成時のコードとロゴの控えが揃っている) */
  revisable: boolean;
}

/** 「AI で修正」の定額 (credit_rate_config)。BrandCutModal の生成側と同じ引き方 */
const MOTION_REVISE_RATE_RESOURCE_TYPE = 'motion-graphics-brand-cut-revise';
const REVISE_INSTRUCTION_MAX = 1000;

interface BrandCut {
  material_id: string;
  material_table: string;
  content_type: string | null;
  video_url: string | null;
  thumbnail_url: string | null;
  width: number | null;
  height: number | null;
  duration_seconds: number | null;
  name: string | null;
  created_at: string;
  motion?: BrandCutMotionSummary | null;
}

const MOTION_SCORE_LABELS: Array<[keyof BrandCutMotionSummary['scores'], string]> = [
  ['logo', 'ロゴ'],
  ['text', '文字'],
  ['colors', '配色'],
  ['completion', '完成度'],
  ['direction', '演出'],
];

/**
 * 採点の 1 行表示。「採点 4.6 · 1回目で合格」/「採点 3.4 · 3回目 (基準未達)」。
 * 基準は平均 4.0 以上かつ全観点 3 以上。修正上限 (2 回) まで届かなければ最高得点の版を採用するので、未達も成果物として出る。
 */
function motionScoreLabel(m: BrandCutMotionSummary): { text: string; title: string } {
  const attempt = m.attempt ? `${m.attempt}回目` : null;
  const verdict = m.passed ? (attempt ? `${attempt}で合格` : '合格') : `${attempt ? `${attempt} ` : ''}(基準未達)`;
  const text = `採点 ${m.average.toFixed(1)} · ${verdict}`;
  const breakdown = MOTION_SCORE_LABELS.filter(([k]) => m.scores[k] != null)
    .map(([k, label]) => `${label} ${m.scores[k]}`)
    .join(' / ');
  const title = `AI の採点 (5 点満点・平均 4.0 以上かつ全観点 3 以上で合格)\n${breakdown}\n採点 ${m.critique_rounds} 回`;
  return { text, title };
}

/** 高さ(px)から 720p / 1080p などの解像度ラベルを求める。標準解像度に近いものへ丸める。 */
const RESOLUTION_TIERS = [360, 480, 540, 720, 1080, 1440, 2160];
function resolutionLabel(width: number | null, height: number | null): string | null {
  const shortSide = width && height ? Math.min(width, height) : height;
  if (!shortSide) return null;
  const tier = RESOLUTION_TIERS.reduce((best, t) =>
    Math.abs(t - shortSide) < Math.abs(best - shortSide) ? t : best,
  );
  return `${tier}p`;
}

/** 秒数を「8秒」のように整形する。小数は四捨五入。 */
function durationLabel(seconds: number | null): string | null {
  if (!seconds || seconds <= 0) return null;
  return `${Math.round(seconds)}秒`;
}

interface BrandCutSectionProps {
  brandId: string;
  brandName: string;
  tagline?: string;
  description?: string;
  brandMainColor?: string;
  brandSubColor?: string;
  brandAccentColor?: string;
  logos: BrandCutModalLogo[];
}

/**
 * ブランド詳細ページの「ブランドカット」セクション。
 * 新規生成ボタン + 生成済みブランドカット動画のサムネイルを表示する。
 * 生成はジョブ化され、グローバルの生成キュー（画面下部のステータスバー）に表示される。
 */
export function BrandCutSection({
  brandId,
  brandName,
  tagline,
  description,
  brandMainColor,
  brandSubColor,
  brandAccentColor,
  logos,
}: BrandCutSectionProps) {
  const toast = useToast();
  const { confirm, dialog } = useConfirmDialog();
  const queryClient = useQueryClient();
  const { setActiveJobs, setShowJobQueueModal } = useGenerationJobs();
  const [chooserOpen, setChooserOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  // クリックされたブランドカットの素材詳細モーダル（パネルではなくモーダルを直接開く）
  const [detailMaterial, setDetailMaterial] = useState<TemporaryMaterial | CloudMaterial | null>(null);
  const [detailTable, setDetailTable] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // 「AI で修正」(モーショングラフィックス生成のみ)
  const [reviseTarget, setReviseTarget] = useState<BrandCut | null>(null);
  const [reviseText, setReviseText] = useState('');
  const [reviseSubmitting, setReviseSubmitting] = useState(false);
  const { data: creditRatesData } = useCreditRates();
  const reviseRate = (() => {
    const rate = creditRatesData?.rates?.find(
      (r) => r.service_type === 'video_generation' && r.resource_type === MOTION_REVISE_RATE_RESOURCE_TYPE,
    );
    return rate ? Number(rate.credits_per_unit) : null;
  })();

  const { data, isLoading } = useQuery({
    queryKey: ['brand-cuts', brandId],
    queryFn: async () => {
      const resp = await apiClient.get<{ brand_cuts: BrandCut[] }>(`/brands/${brandId}/brand-cuts`);
      return resp.data.brand_cuts;
    },
    enabled: !!brandId,
  });

  const cuts = data ?? [];

  const launchJob = async (params: BrandCutGenerationParams) => {
    try {
      const { job_id } = await createBrandCutGenerationJob(params);
      // グローバルキューに登録 → 画面下部のステータスバーに進捗が出る
      setActiveJobs((prev) => [
        ...prev,
        { jobId: job_id, status: 'polling' as const, startedAt: Date.now(), type: 'video' as const, label: 'ブランドカット' },
      ]);
      setShowJobQueueModal(true);

      // 完了をバックグラウンドで待機（モーダルは閉じても継続）
      // 別タブ・最小化中も完了検知を継続しブラウザ通知を出す
      waitForJobCompletion<BrandCutGenerationResult>(job_id, {
        skipVisibilityCheck: true,
        // 段階表示と、モーションのこだわり生成の候補 / 中断の受付を生成キューの行に写す (§10.10.1)
        onProgress: (jobInfo) => {
          setActiveJobs((prev) => prev.map((j) => (j.jobId === job_id ? { ...j, ...motionJobProgressPatch(jobInfo) } : j)));
        },
      })
        .then((job) => {
          const result = job.result_data;
          setActiveJobs((prev) =>
            prev.map((j) =>
              j.jobId === job_id
                ? {
                    ...j,
                    status: 'completed' as const,
                    result: {
                      id: job_id,
                      material_id: result?.material_id ?? undefined,
                      image_url: result?.thumbnail_url ?? undefined,
                    },
                  }
                : j,
            ),
          );
          // サムネイル一覧を更新
          queryClient.invalidateQueries({ queryKey: ['brand-cuts', brandId] });
        })
        .catch((err: unknown) => {
          const msg = err instanceof Error ? err.message : 'ブランドカット動画の生成に失敗しました';
          setActiveJobs((prev) =>
            prev.map((j) => (j.jobId === job_id ? { ...j, status: 'failed' as const, error: msg } : j)),
          );
        });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'ジョブの作成に失敗しました';
      toast.error(msg);
    }
  };

  // 「AI で修正」: 指示文を付けて修正ジョブを作る。完了すると修正版が新しいカードとして並ぶ (元は残る)
  const submitRevise = async () => {
    if (!reviseTarget) return;
    const instruction = reviseText.trim();
    if (!instruction) {
      toast.error('修正の指示を入力してください');
      return;
    }
    setReviseSubmitting(true);
    try {
      const { job_id } = await createBrandCutReviseJob({ material_id: reviseTarget.material_id, instruction });
      setActiveJobs((prev) => [
        ...prev,
        { jobId: job_id, status: 'polling' as const, startedAt: Date.now(), type: 'video' as const, label: 'ブランドカット (AI で修正)' },
      ]);
      setShowJobQueueModal(true);
      setReviseTarget(null);
      setReviseText('');
      waitForJobCompletion<BrandCutGenerationResult>(job_id, { skipVisibilityCheck: true })
        .then((job) => {
          const result = job.result_data;
          setActiveJobs((prev) =>
            prev.map((j) =>
              j.jobId === job_id
                ? { ...j, status: 'completed' as const, result: { id: job_id, material_id: result?.material_id ?? undefined, image_url: result?.thumbnail_url ?? undefined } }
                : j,
            ),
          );
          queryClient.invalidateQueries({ queryKey: ['brand-cuts', brandId] });
        })
        .catch((err: unknown) => {
          const msg = err instanceof Error ? err.message : 'AI での修正に失敗しました';
          setActiveJobs((prev) => prev.map((j) => (j.jobId === job_id ? { ...j, status: 'failed' as const, error: msg } : j)));
        });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err instanceof Error ? err.message : 'ジョブの作成に失敗しました');
      toast.error(msg);
    } finally {
      setReviseSubmitting(false);
    }
  };

  // 既存素材（動画）をこのブランドのブランドカットとして関連付ける。
  // 個人フォルダの素材は 409(requires_move_to_shared) が返るので、同意を得て
  // 共有フォルダへ移動 (move_to_shared) してから再関連付けする。
  const attachExisting = async (materialId: string) => {
    try {
      await apiClient.post(`/brands/${brandId}/brand-cuts`, { material_id: materialId });
      queryClient.invalidateQueries({ queryKey: ['brand-cuts', brandId] });
      toast.success('ブランドカットに関連付けました');
    } catch (err: unknown) {
      const resp = (err as { response?: { status?: number; data?: { error?: string; requires_move_to_shared?: boolean } } })?.response;
      if (resp?.status === 409 && resp.data?.requires_move_to_shared) {
        if (!(await confirm({ title: 'この素材は個人フォルダにあります', message: '共有フォルダに移動してブランドカットに追加しますか？', confirmLabel: '移動して追加', variant: 'warning' }))) {
          return;
        }
        try {
          await apiClient.post(`/brands/${brandId}/brand-cuts`, { material_id: materialId, move_to_shared: true });
          queryClient.invalidateQueries({ queryKey: ['brand-cuts', brandId] });
          toast.success('共有フォルダに移動してブランドカットに追加しました');
        } catch (err2: unknown) {
          const msg2 =
            (err2 as { response?: { data?: { error?: string } } })?.response?.data?.error ||
            (err2 instanceof Error ? err2.message : '関連付けに失敗しました');
          toast.error(msg2);
        }
        return;
      }
      const msg = resp?.data?.error || (err instanceof Error ? err.message : '関連付けに失敗しました');
      toast.error(msg);
    } finally {
      setPickerOpen(false);
    }
  };

  // ローカルから動画をアップロードして関連付ける
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // 同じファイルの再選択を許可
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      toast.error('動画ファイルを選択してください');
      return;
    }
    setUploading(true);
    try {
      const uploaded = await uploadTemporaryMaterial({ file });
      // ブランドカット素材は共有フォルダに統一する。アップロードは新規作成なので
      // 確認なしで共有フォルダへ昇格 (move_to_shared) する。
      await apiClient.post(`/brands/${brandId}/brand-cuts`, { material_id: uploaded.id, move_to_shared: true });
      queryClient.invalidateQueries({ queryKey: ['brand-cuts', brandId] });
      toast.success('動画を共有フォルダに追加しました');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err instanceof Error ? err.message : 'アップロードに失敗しました');
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  // ブランドカットから外す（紐付け解除のみ・素材は残る）
  const removeCut = async (materialId: string) => {
    if (!(await confirm({ title: 'このブランドカットを外しますか？', message: '動画素材自体は削除されません。', confirmLabel: '外す' }))) return;
    try {
      await apiClient.delete(`/brands/${brandId}/brand-cuts/${materialId}`);
      queryClient.invalidateQueries({ queryKey: ['brand-cuts', brandId] });
      toast.success('ブランドカットから外しました');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err instanceof Error ? err.message : '解除に失敗しました');
      toast.error(msg);
    }
  };

  // 素材の名前を変更する（素材テーブルに応じて PATCH を出し分け）
  const renameCut = async (cut: BrandCut, newName: string) => {
    try {
      if (cut.material_table === 'materials') {
        await updateCloudMaterial(cut.material_id, { name: newName || null });
      } else {
        await updateTemporaryMaterial(cut.material_id, { name: newName || null });
      }
      queryClient.invalidateQueries({ queryKey: ['brand-cuts', brandId] });
      toast.success('名前を更新しました');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err instanceof Error ? err.message : '名前の更新に失敗しました');
      toast.error(msg);
    }
  };

  // サムネイルクリック: 素材を取得して詳細モーダルを直接開く
  const openDetail = async (cut: BrandCut) => {
    try {
      const material = await fetchMaterialById(cut.material_id);
      if (!material) {
        toast.error('素材が見つかりませんでした');
        return;
      }
      setDetailMaterial(material);
      setDetailTable(cut.material_table);
    } catch {
      toast.error('素材の取得に失敗しました');
    }
  };
  const closeDetail = () => {
    setDetailMaterial(null);
    setDetailTable(null);
  };

  // 追加方法の選択
  const chooseAI = () => {
    setChooserOpen(false);
    setModalOpen(true);
  };
  const chooseExisting = () => {
    setChooserOpen(false);
    setPickerOpen(true);
  };
  const chooseUpload = () => {
    setChooserOpen(false);
    fileInputRef.current?.click();
  };

  return (
    <>
      {dialog}
      <div className="card brand-cut-card">
        <h2 className="card-title" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <img src="/vintage_tv_4.svg" alt="" aria-hidden="true" className="card-title-icon" style={{ marginRight: 0 }} />
          ブランドカット
          <HelpTooltip ariaLabel="ブランドカットのヘルプ">
            ロゴを主役にした、CMの冒頭/末尾に差し込む短いブランドカット動画（ロゴアニメーション）を生成します。ロゴ・ブランド名・スローガン・ブランドカラー・自由入力のイメージをもとにAIが作成します。生成物はライブラリの共有フォルダに「ブランドカット」タグ付きで保存され、このブランドに紐付きます。
          </HelpTooltip>
        </h2>

        {isLoading ? (
          <div className="loading-stats">読み込み中...</div>
        ) : cuts.length === 0 ? (
          <p className="brand-empty-note">ブランドカットが登録されていません。</p>
        ) : (
          <div className="brand-cut-grid">
            {cuts.map((c) => (
              <BrandCutTile
                key={`${c.material_table}-${c.material_id}`}
                cut={c}
                onOpenDetail={() => openDetail(c)}
                onRemove={() => removeCut(c.material_id)}
                onRename={(newName) => renameCut(c, newName)}
                onRevise={c.motion?.revisable ? () => { setReviseText(''); setReviseTarget(c); } : undefined}
              />
            ))}
          </div>
        )}
        {/* 追加はグリッドの下の共通の「〜を追加」ボタン (ロゴ・サウンドロゴ等と同じ見た目・利用者指示 2026-10-30)。クリックで追加方法を選択 */}
        {!isLoading && (
          <div className="brand-add-row">
            <button type="button" className="brand-add-button" onClick={() => setChooserOpen(true)} disabled={uploading}>
              <Plus size={14} aria-hidden="true" />
              {uploading ? 'アップロード中…' : 'ブランドカットを追加'}
            </button>
          </div>
        )}
      </div>

      {/* 「AI で修正」の指示入力 */}
      {reviseTarget &&
        createPortal(
          <div className="modal-overlay modal-overlay--nested">
            <div className="modal-card modal-card--sm">
              <div className="modal-header">
                <h2 className="modal-title">AI で修正</h2>
                <button type="button" className="modal-close-button" onClick={() => setReviseTarget(null)} aria-label="閉じる" disabled={reviseSubmitting}>
                  ×
                </button>
              </div>
              <div className="brand-cut-revise">
                <p className="brand-cut-revise-desc">
                  {reviseTarget.name ? `「${reviseTarget.name}」` : 'このブランドカット'}への修正の指示を書いてください。演出の骨格は保ったまま、指示した点だけを直した版を新しいブランドカットとして追加します (元の動画は残ります)。
                </p>
                <textarea
                  className="brand-cut-revise-textarea"
                  value={reviseText}
                  maxLength={REVISE_INSTRUCTION_MAX}
                  rows={4}
                  placeholder="例: ロゴの登場をもっとゆっくりにして、タグラインは最後に小さく出す"
                  onChange={(e) => setReviseText(e.target.value)}
                  disabled={reviseSubmitting}
                  autoFocus
                />
                <div className="brand-cut-revise-footer">
                  <span className="brand-cut-revise-cost">
                    {reviseRate === null ? '料金が設定されていないため現在ご利用いただけません' : `${reviseRate} ポイント / 回`}
                  </span>
                  <button
                    type="button"
                    className="brand-cut-revise-submit"
                    onClick={submitRevise}
                    disabled={reviseSubmitting || reviseRate === null || reviseText.trim().length === 0}
                  >
                    {reviseSubmitting ? '送信中…' : '修正する'}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      <BrandCutModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        brandId={brandId}
        brandName={brandName}
        tagline={tagline}
        description={description}
        brandMainColor={brandMainColor}
        brandSubColor={brandSubColor}
        brandAccentColor={brandAccentColor}
        logos={logos}
        onSubmit={launchJob}
      />

      {pickerOpen && (
        // スタジオのストーリーボード「末尾エンドカードの動画素材を選択」等と同じ共有モーダル。
        // ブランド/アスペクト比フィルタはデフォルト無し（brandId / aspectRatio を渡さない）。
        <MaterialsPickerModal
          title="ブランドカットにする動画を選択"
          // 既に紐付け済みのブランドカットはピッカーから除外する
          excludeMaterialIds={cuts.map((c) => c.material_id)}
          onSelect={(_url, materialId) => {
            if (materialId) attachExisting(materialId);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {/* 素材詳細モーダル（サムネイルクリックで開く）。素材テーブルに応じて出し分け。 */}
      {detailMaterial && detailTable === 'materials' && (
        <CloudMaterialsModal
          material={detailMaterial as CloudMaterial}
          onClose={closeDetail}
          onRefresh={(m) => {
            queryClient.invalidateQueries({ queryKey: ['brand-cuts', brandId] });
            if (m) setDetailMaterial(m);
            else closeDetail();
          }}
        />
      )}
      {detailMaterial && detailTable === 'temporary_materials' && (
        <TemporaryPreviewModal
          material={detailMaterial as TemporaryMaterial}
          onClose={closeDetail}
          onRefresh={(m) => {
            queryClient.invalidateQueries({ queryKey: ['brand-cuts', brandId] });
            if (m) setDetailMaterial(m);
            else closeDetail();
          }}
        />
      )}

      {/* ローカル動画アップロード用の隠し input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* 追加方法の選択ポップアップ */}
      {chooserOpen &&
        createPortal(
          // 誤操作で閉じないよう、オーバーレイのクリックでは閉じない (✕ ボタンのみ)
          <div className="modal-overlay modal-overlay--nested">
            <div className="modal-card modal-card--sm">
              <div className="modal-header">
                <h2 className="modal-title">ブランドカットを追加</h2>
                <button
                  type="button"
                  className="modal-close-button"
                  onClick={() => setChooserOpen(false)}
                  aria-label="閉じる"
                >
                  ×
                </button>
              </div>
              <div className="brand-cut-chooser">
                <button type="button" className="brand-cut-chooser-option" onClick={chooseAI}>
                  <img src="/reward_stars_2.svg" alt="" aria-hidden="true" className="brand-cut-chooser-icon" />
                  <span className="brand-cut-chooser-text">
                    <span className="brand-cut-chooser-title">AIで生成</span>
                    <span className="brand-cut-chooser-desc">ロゴから新しいブランドカット動画を生成します</span>
                  </span>
                </button>
                <button type="button" className="brand-cut-chooser-option" onClick={chooseExisting}>
                  <img src="/book_search.svg" alt="" aria-hidden="true" className="brand-cut-chooser-icon" />
                  <span className="brand-cut-chooser-text">
                    <span className="brand-cut-chooser-title">既存の素材を関連付け</span>
                    <span className="brand-cut-chooser-desc">ライブラリの動画素材をこのブランドに紐付けます</span>
                  </span>
                </button>
                <button type="button" className="brand-cut-chooser-option" onClick={chooseUpload}>
                  <img src="/export_file.svg" alt="" aria-hidden="true" className="brand-cut-chooser-icon" />
                  <span className="brand-cut-chooser-text">
                    <span className="brand-cut-chooser-title">ローカルからアップロード</span>
                    <span className="brand-cut-chooser-desc">手元の動画ファイルをアップロードして追加します</span>
                  </span>
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * ブランドカット動画タイル。サムネイル(静止画=最初のフレーム)を表示し、クリックで素材モーダルを開く。
 * 動画は埋め込まない（再生済み video が Safari で他要素のクリックを阻害する問題を回避）。
 */
// 縦長の高さ上限: 16:9 の高さの2倍まで → アスペクト比(w/h)の下限は 8/9。
// 例) 9:16 (w/h=0.5625) はこの値までクランプされ、高さが 16:9 の2倍に収まる。
const MIN_TILE_RATIO = 8 / 9;

function BrandCutTile({
  cut,
  onOpenDetail,
  onRemove,
  onRename,
  onRevise,
}: {
  cut: BrandCut;
  onOpenDetail: () => void;
  onRemove: () => void;
  onRename: (newName: string) => void;
  /** 「AI で修正」(モーショングラフィックス生成で、コードとロゴの控えがあるときだけ) */
  onRevise?: () => void;
}) {
  // 実寸からアスペクト比を決める（不明時は CSS の 16:9 フォールバック）。
  // 縦長は高さ上限のため下限比でクランプ（はみ出し分は contain でレターボックス表示）。
  const ratioStyle =
    cut.width && cut.height
      ? { aspectRatio: `${Math.max(cut.width / cut.height, MIN_TILE_RATIO)}` }
      : undefined;

  const duration = durationLabel(cut.duration_seconds);
  const resolution = resolutionLabel(cut.width, cut.height);
  const metaParts = [duration, resolution].filter(Boolean);

  // 名前のその場リネーム (Enter/フォーカス外しで確定、Escでキャンセル)
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(cut.name ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const startEditing = () => {
    setDraftName(cut.name ?? '');
    setEditing(true);
  };
  const commit = () => {
    setEditing(false);
    const trimmed = draftName.trim();
    if (trimmed !== (cut.name ?? '')) onRename(trimmed);
  };
  const cancel = () => {
    setEditing(false);
    setDraftName(cut.name ?? '');
  };

  return (
    <div className="brand-cut-item">
      <div className="brand-cut-tile" style={ratioStyle}>
        <button
          type="button"
          className="brand-cut-tile-play-area"
          onClick={onOpenDetail}
          title="素材モーダルで開く"
        >
          {cut.thumbnail_url ? (
            <img src={cut.thumbnail_url} alt={cut.name || 'ブランドカット'} />
          ) : (
            <div className="brand-cut-tile-placeholder" />
          )}
          <span className="brand-cut-tile-play" aria-hidden="true">▶</span>
        </button>
        <button
          type="button"
          className="brand-cut-tile-remove"
          onClick={onRemove}
          title="ブランドカットから外す"
          aria-label="ブランドカットから外す"
        >
          <Trash2 size={13} aria-hidden="true" />
        </button>
        {onRevise && (
          <button
            type="button"
            className="brand-cut-tile-revise"
            onClick={onRevise}
            title="AI で修正"
            aria-label="AI で修正"
          >
            <Sparkles size={13} aria-hidden="true" />
          </button>
        )}
      </div>
      <div className="brand-cut-name-row">
        {editing ? (
          <input
            ref={inputRef}
            type="text"
            className="brand-cut-name-input"
            value={draftName}
            maxLength={200}
            placeholder="名前を入力"
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') cancel();
            }}
          />
        ) : (
          <>
            <span className="brand-cut-name" title={cut.name ?? undefined}>
              {cut.name || ''}
            </span>
            <button
              type="button"
              className="brand-cut-name-edit"
              onClick={startEditing}
              title="名前を変更"
              aria-label="名前を変更"
            >
              <Pencil size={12} aria-hidden="true" />
            </button>
          </>
        )}
      </div>
      {(metaParts.length > 0 || cut.motion) && (
        <div className="brand-cut-meta">
          {metaParts.map((part, i) => (
            <span key={i} className="brand-cut-meta-item">{part}</span>
          ))}
          {cut.motion && (() => {
            const { text, title } = motionScoreLabel(cut.motion);
            return (
              <span
                className={`brand-cut-meta-item brand-cut-meta-score${cut.motion.passed ? '' : ' brand-cut-meta-score--failed'}`}
                title={title}
              >
                {text}
              </span>
            );
          })()}
        </div>
      )}
    </div>
  );
}
